import type { VenueOSClient } from '@mappedin/venueos-applet';
import { useCallback, useEffect, useRef } from 'react';
import { layerManager } from '../layers/layer-manager';
import { initStructuralPipes, rebuildFloorPipes } from '../layers/structural-pipes';
import { loadVenueMap, type Venue } from '../lib/map-helpers';
import {
  isFloorChangeEvent,
  type MappedinFloor,
  type MappedinLocation,
  type MappedinMapData,
  type MappedinMapView,
  type MappedinSpace,
} from '../lib/mappedin-types';
import { rehideOutdoorNonDynamic, useAppStore } from '../state/app-store';

function hasStringId(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false;
  if (!('id' in value)) return false;
  const rec = value as Record<string, unknown>;
  return typeof rec.id === 'string';
}

// Custom type predicates below. Mappedin's `getByType` returns `unknown[]`
// at compile time but every entity we consume here carries a stable string
// `id`, so these predicates use `hasStringId` as the runtime check and
// declare the appropriate SDK shape for the type checker.
function isFloor(v: unknown): v is MappedinFloorWithName {
  return hasStringId(v);
}
function isSpace(v: unknown): v is SpaceLike {
  return hasStringId(v);
}
function isLocation(v: unknown): v is MappedinLocation {
  return hasStringId(v);
}

function getFloors(md: MappedinMapData): MappedinFloorWithName[] {
  return md.getByType('floor').filter(isFloor);
}
function getSpaces(md: MappedinMapData): SpaceLike[] {
  return md.getByType('space').filter(isSpace);
}
function getLocations(md: MappedinMapData, type: string): MappedinLocation[] {
  return md.getByType(type).filter(isLocation);
}

const GCW = Math.ceil(1350 * 0.42 * 0.9) + 20;
const HIDDEN_LAYER_TERMS = ['landscape', 'tree', 'exterior'];
const OUTDOOR_TERMS = ['outdoor', 'outside', 'exterior', 'landscape'];

interface MappedinFloorWithName extends MappedinFloor {
  name?: string;
}

function isOutdoorFloor(f: MappedinFloorWithName): boolean {
  const n = (f.name ?? '').toLowerCase();
  return OUTDOOR_TERMS.some((t) => n.includes(t));
}

function screenOffsets(): { top: number; bottom: number; left: number; right: number } {
  return window.innerWidth < 768
    ? { top: 10, bottom: 10, left: 10, right: 10 }
    : { top: 40, bottom: 40, left: GCW, right: 40 };
}

function hideIds(mv: MappedinMapView, allSpaces: MappedinSpace[], ids: Set<string>): void {
  for (const sp of allSpaces) {
    if (ids.has(sp.id)) {
      try {
        mv.updateState(sp, { visible: false, interactive: false });
      } catch {
        // Ignore.
      }
    }
  }
}

interface RoomCoord {
  space: MappedinSpace;
  lat: number;
  lng: number;
  dist?: number;
}

function pcaBearing(co: RoomCoord[], cLat: number, cLng: number, lS: number): number {
  let xx = 0;
  let yy = 0;
  let xy = 0;
  for (const p of co) {
    const dx = (p.lng - cLng) * lS;
    const dy = p.lat - cLat;
    xx += dx * dx;
    yy += dy * dy;
    xy += dx * dy;
  }
  const a = 0.5 * Math.atan2(2 * xy, xx - yy);
  const lb = Math.PI / 2 - a;
  const candidates = [
    lb + Math.PI / 4,
    lb + (3 * Math.PI) / 4,
    lb + (5 * Math.PI) / 4,
    lb + (7 * Math.PI) / 4,
  ];
  const pb = lb + Math.PI / 2;
  const pdx = Math.sin(pb);
  const pdy = Math.cos(pb);
  let sA = 0;
  let sB = 0;
  for (const p of co) {
    const d2x = (p.lng - cLng) * lS;
    const d2y = p.lat - cLat;
    if (d2x * pdx + d2y * pdy >= 0) sA++;
    else sB++;
  }
  const id = (sA >= sB ? pb : pb + Math.PI) - Math.PI;
  let best = candidates[0] ?? lb;
  let bd = Infinity;
  for (const c of candidates) {
    let diff = id - c;
    diff = ((((diff + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI;
    if (Math.abs(diff) < bd) {
      bd = Math.abs(diff);
      best = c;
    }
  }
  return ((((best * 180) / Math.PI) % 360) + 360) % 360;
}

interface SpaceLike extends MappedinSpace {
  center?: { latitude: number; longitude: number };
  type?: string;
}

interface LayerDef {
  name?: string;
  spaces?: Array<{ spaceId?: string }>;
}

function classifyFloors(sf: MappedinFloorWithName[]): {
  multi: boolean;
  od: MappedinFloorWithName[];
  bf: MappedinFloorWithName[];
} {
  const elevCount: Record<number, number> = {};
  for (const f of sf) {
    const e = f.elevation ?? 0;
    elevCount[e] = (elevCount[e] ?? 0) + 1;
  }
  let multi = false;
  for (const k of Object.keys(elevCount)) {
    if ((elevCount[Number(k)] ?? 0) > 1) {
      multi = true;
      break;
    }
  }
  const od = multi ? sf.filter(isOutdoorFloor) : [];
  const bf = multi ? sf.filter((f) => !isOutdoorFloor(f)) : sf;
  return { multi, od, bf };
}

function extractLayersInfo(md: MappedinMapData): {
  dynIds: Set<string>;
  hidden: Set<string>;
} {
  const dynIds = new Set<string>();
  const hidden = new Set<string>();
  try {
    const layers = md.internal?.mvf?.enterprise?.layers;
    if (Array.isArray(layers)) {
      for (const ly of layers as LayerDef[]) {
        const ln = (ly.name ?? '').toLowerCase();
        const spaces = ly.spaces ?? [];
        if (ln.includes('dynamic')) {
          for (const dsp of spaces) if (dsp.spaceId) dynIds.add(dsp.spaceId);
        }
        if (HIDDEN_LAYER_TERMS.some((t) => ln.includes(t))) {
          for (const dsp of spaces) if (dsp.spaceId) hidden.add(dsp.spaceId);
        }
      }
    }
  } catch {
    // Ignore — internal surface not available for this MVF.
  }
  return { dynIds, hidden };
}

function computeNonDynOutdoor(
  multi: boolean,
  od: MappedinFloorWithName[],
  dynIds: Set<string>,
  hidden: Set<string>,
  allSpaces: SpaceLike[],
): MappedinSpace[] | null {
  if (!multi || od.length === 0 || dynIds.size === 0) return null;
  const odSet: Record<string, boolean> = {};
  for (const fl of od) odSet[fl.id] = true;
  const out: MappedinSpace[] = [];
  for (const sp of allSpaces) {
    if (sp.floor && odSet[sp.floor.id] && !dynIds.has(sp.id)) {
      hidden.add(sp.id);
      out.push(sp);
    }
  }
  return out;
}

function collectUnderground(
  bf: MappedinFloorWithName[],
  allSpaces: SpaceLike[],
  hidden: Set<string>,
  mv: MappedinMapView,
): { ugSpaces: MappedinSpace[]; ugColors: Record<string, string | null> } {
  const ugSpaces: MappedinSpace[] = [];
  const ugColors: Record<string, string | null> = {};
  const bfIds: Record<string, boolean> = {};
  for (const f of bf) {
    if ((f.elevation ?? 0) < 0) bfIds[f.id] = true;
  }
  for (const sp of allSpaces) {
    if (sp.floor && bfIds[sp.floor.id] && !hidden.has(sp.id)) {
      ugSpaces.push(sp);
      try {
        const gs = mv.getState?.(sp);
        ugColors[sp.id] = gs?.color ?? null;
      } catch {
        ugColors[sp.id] = null;
      }
    }
  }
  return { ugSpaces, ugColors };
}

function buildRoomCoords(rooms: MappedinSpace[]): RoomCoord[] {
  const coords: RoomCoord[] = [];
  for (const room of rooms) {
    const ct = (room as SpaceLike).center;
    if (ct && typeof ct.latitude === 'number') {
      coords.push({ space: room, lat: ct.latitude, lng: ct.longitude });
    }
  }
  return coords;
}

function computeFocusBearing(
  rooms: MappedinSpace[],
  allSpaces: SpaceLike[],
  hidden: Set<string>,
  mv: MappedinMapView,
): { focus: MappedinSpace[]; bearing: number } {
  const coords = buildRoomCoords(rooms);
  if (coords.length < 2) return { focus: rooms, bearing: 45 };
  let sLat = 0;
  let sLng = 0;
  for (const p of coords) {
    sLat += p.lat;
    sLng += p.lng;
  }
  const cLat = sLat / coords.length;
  const cLng = sLng / coords.length;
  const lS = Math.cos((cLat * Math.PI) / 180);
  for (const p of coords) {
    const dl = p.lat - cLat;
    const dn = (p.lng - cLng) * lS;
    p.dist = Math.sqrt(dl * dl + dn * dn);
  }
  coords.sort((a, b) => (a.dist ?? 0) - (b.dist ?? 0));
  const cut = Math.max(10, Math.floor(coords.length * 0.9));
  const core = coords.slice(0, cut);
  const outliers = coords.slice(cut);
  const focus = core.map((c) => c.space);
  for (const o of outliers) hidden.add(o.space.id);
  hideIds(mv, allSpaces, hidden);
  const bearing = pcaBearing(core, cLat, cLng, lS);
  useAppStore.getState().setAutoBearing(bearing);
  return { focus, bearing };
}

function applyInitialFloorState(
  mv: MappedinMapView,
  af: MappedinFloorWithName[],
  bf: MappedinFloorWithName[],
  multi: boolean,
): void {
  const FACADE_GAP = 4;
  const uElev: number[] = [];
  const seenE: Record<number, boolean> = {};
  for (const f of bf) {
    const e = f.elevation ?? 0;
    if (!seenE[e]) {
      seenE[e] = true;
      uElev.push(e);
    }
  }
  uElev.sort((a, b) => a - b);
  const eOrd: Record<number, number> = {};
  uElev.forEach((e, i) => {
    eOrd[e] = i;
  });
  const groundOrd = eOrd[0] ?? 0;
  for (const fl of bf) {
    const ord = eOrd[fl.elevation ?? 0] ?? 0;
    const isBsmt = (fl.elevation ?? 0) < 0;
    mv.updateState(fl, {
      visible: !isBsmt,
      altitude: (ord - groundOrd) * FACADE_GAP,
      geometry: { visible: !isBsmt, opacity: 1.0 },
      labels: { enabled: false },
      markers: { enabled: true, occlude: false },
    });
  }
  if (multi) {
    for (const f of af) {
      if (isOutdoorFloor(f)) {
        mv.updateState(f, {
          visible: true,
          altitude: 0,
          geometry: { visible: true, opacity: 1 },
          labels: { enabled: false },
          markers: { enabled: false },
        });
      }
    }
  }
  rehideOutdoorNonDynamic(mv);
}

interface StackedAnimCtx {
  mv: MappedinMapView;
  allSpaces: SpaceLike[];
  hidden: Set<string>;
  focus: MappedinSpace[];
  bearing: number;
}

function scheduleStackedAnim(ctx: StackedAnimCtx): void {
  const { mv, allSpaces, hidden, focus, bearing } = ctx;
  setTimeout(() => {
    hideIds(mv, allSpaces, hidden);
    useAppStore.getState().setIsStacked(true);
    // Build pipes synchronously now (tints were applied up-front in
    // `processLoadedMap`, so this is just `Shapes.add` calls — fast and
    // safe to run before the altitude tween starts). Without this the
    // pipes only appeared after the store-subscriber debounce fired,
    // hundreds of ms after the stack animation was already on screen.
    rebuildFloorPipes();
    setTimeout(() => useAppStore.getState().applyStackedView(), 50);
    setTimeout(() => {
      hideIds(mv, allSpaces, hidden);
      const p2 = useAppStore.getState().pitch || 60;
      if (focus.length > 0) {
        void mv.Camera.focusOn(focus, { duration: 800, pitch: p2, bearing });
      }
    }, 1200);
    setTimeout(() => hideIds(mv, allSpaces, hidden), 900);
  }, 1000);
}

interface ProcessCtx {
  mv: MappedinMapView;
  md: MappedinMapData;
  v: Venue;
}

function processLoadedMap({ mv, md, v }: ProcessCtx): void {
  const cfg = useAppStore.getState();
  mv.updateGlobalState({ backgroundColor: cfg.darkBackground ? '#0e1320' : '#1a1f2e' });
  mv.Camera.setScreenOffsets?.(screenOffsets());

  const af = getFloors(md);
  const sf = af.slice().sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0));
  const { multi, od, bf } = classifyFloors(sf);

  const isCms = v.id.startsWith('cms:');
  const locs = getLocations(md, isCms ? 'enterprise-location' : 'location-profile');
  const cats: unknown[] = isCms
    ? md.getByType('enterprise-category')
    : md.getByType('location-category');

  const store = useAppStore.getState();
  store.setIsMultiBuilding(multi);
  store.setOutdoorFloors(od);
  store.setFloors(bf);
  store.setActiveFloor(bf[0] ?? null);
  store.setLocations(locs);
  store.setCategories(cats);
  store.setMapState(mv, md);

  const allSpaces = getSpaces(md);
  const { dynIds, hidden } = extractLayersInfo(md);
  const nonDynOutdoor = computeNonDynOutdoor(multi, od, dynIds, hidden, allSpaces);

  hideIds(mv, allSpaces, hidden);

  const { ugSpaces, ugColors } = collectUnderground(bf, allSpaces, hidden, mv);
  useAppStore.getState().setSpaceTracking({
    hiddenSpaceIds: hidden,
    dynamicSpaceIds: dynIds,
    undergroundSpaces: ugSpaces,
    undergroundOrigColors: ugColors,
    nonDynOutdoorSpaces: nonDynOutdoor,
  });

  const rooms: MappedinSpace[] = [];
  for (const sp of allSpaces) {
    if (!hidden.has(sp.id) && sp.type === 'room') rooms.push(sp);
  }
  const { focus, bearing } = computeFocusBearing(rooms, allSpaces, hidden, mv);

  mv.manualFloorVisibility = true;
  applyInitialFloorState(mv, af, bf, multi);
  useAppStore.getState().setIsStacked(false);

  // Tint spaces up-front, synchronously, BEFORE the stack animation
  // kicks off. Running `applyTint`'s hundreds of `updateState(space,
  // { color })` calls in the middle of the 600ms altitude tween that
  // `applyStackedView` starts was starving the tween and decoupling
  // the floor geometry from its markers. `rebuildFloorPipes(true)`
  // with `isStacked=false` falls through to the tint-only branch.
  rebuildFloorPipes(true);

  const pitch = useAppStore.getState().pitch || 60;
  if (focus.length > 0) {
    void mv.Camera.focusOn(focus, { duration: 600, pitch, bearing });
  }

  scheduleStackedAnim({ mv, allSpaces, hidden, focus, bearing });

  mv.on('floor-change', (evt: unknown) => {
    if (!isFloorChangeEvent(evt)) return;
    useAppStore.getState().setActiveFloor(evt.floor);
    setTimeout(() => hideIds(mv, allSpaces, hidden), 100);
  });
}

interface VenueMapProps {
  client: VenueOSClient | null;
}

export function VenueMap({ client }: VenueMapProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MappedinMapView | null>(null);
  // Monotonically-increasing token identifying the in-flight `loadVenueMap`
  // call. Used to discard results from a previous load when the user
  // switches venues faster than the MVF download + hydrate can complete.
  const loadGenRef = useRef(0);
  const venue = useAppStore((s) => s.selectedVenue);
  const loading = useAppStore((s) => s.mapLoading);
  const error = useAppStore((s) => s.mapError);

  useEffect(() => {
    initStructuralPipes();
  }, []);

  // Tears down the live mapView (if any) and bumps the load generation
  // so any in-flight `loadVenueMap` promise is discarded when it
  // settles. Co-locating the bump with the teardown means the cleanup
  // `useEffect` below doesn't have to touch ref `.current` values
  // directly (which the react-hooks lint rightfully flags as racy for
  // DOM refs, even though this one is a monotonic counter).
  const tearDownCurrentMap = useCallback(() => {
    loadGenRef.current++;
    const prev = mapRef.current;
    if (prev) {
      try {
        // `destroy()` cancels all in-flight tweens, tears down the
        // WebGL context, and removes the SDK's window / document
        // listeners. Skipping this (and only clearing `innerHTML`)
        // left the previous venue's altitude tweens, resize handlers,
        // and WebGL context alive — which is why the first venue
        // expanded correctly but every subsequent venue broke: the
        // zombie tweens from the old map were still running inside
        // the SDK's shared tween ticker when the new map tried to
        // animate its own floors.
        prev.destroy?.();
      } catch {
        // SDK may be mid-teardown already; nothing useful we can do.
      }
      mapRef.current = null;
    }
    if (containerRef.current) {
      try {
        containerRef.current.innerHTML = '';
      } catch {
        // Ignore DOM reset errors.
      }
    }
  }, []);

  const load = useCallback(
    (v: Venue | null) => {
      if (!v || !containerRef.current || !client) return;
      tearDownCurrentMap();
      layerManager.clearAll();
      const st = useAppStore.getState();
      // Clear the map view/data FIRST so any plugins subscribed to
      // store changes (e.g. structural pipes) see a consistent
      // "no map" state during the async load, instead of a
      // half-updated store where the old map view coexists with
      // newly-cleared floors or newly-set floors for a different
      // venue.
      st.clearMapState();
      st.setMapLoading(true);
      st.setFloors([]);
      st.setActiveFloor(null);
      st.setLocations([]);
      st.setCategories([]);

      // `tearDownCurrentMap` already bumped `loadGenRef.current`, so
      // reading it here gives us the generation of THIS load.
      const gen = loadGenRef.current;

      // `multiFloorView.enabled: false` is critical. The Mappedin SDK's
      // default `multiFloorView` is `{ enabled: true, floorGap: "auto" }`,
      // which means the SDK actively recomputes every floor's altitude
      // each frame based on its own "auto" stacking rules. With that on,
      // our `animateState(floor, { altitude })` tweens are silently
      // overridden: the top-level floor altitude we write is clobbered
      // back by the auto-stacker, the floor geometry never visibly rises
      // or collapses, and markers (which we translate manually via
      // `updateState` per frame) drift away from the footprint. Setting
      // `manualFloorVisibility = true` only opts out of the
      // floor-change visibility logic — it does NOT disable the
      // auto-stacker. Turning `multiFloorView` off is the only way to
      // own floor altitudes ourselves and match the App Builder export's
      // stack / flat / gap-slider behavior.
      void loadVenueMap(client, containerRef.current, v, {
        outdoorView: { enabled: false },
        multiFloorView: { enabled: false },
      })
        .then((r) => {
          // A newer load has already started — discard this stale
          // result. We destroy the orphaned mapView to avoid leaking
          // its WebGL context / tween loop.
          if (gen !== loadGenRef.current) {
            try {
              r.mapView.destroy?.();
            } catch {
              // Ignore.
            }
            return;
          }
          mapRef.current = r.mapView;
          processLoadedMap({ mv: r.mapView, md: r.mapData, v });
        })
        .catch((err: unknown) => {
          if (gen !== loadGenRef.current) return;
          const message = err instanceof Error ? err.message : 'Failed to load map';
          useAppStore.getState().setMapError(message);
        });
    },
    [client, tearDownCurrentMap],
  );

  useEffect(() => {
    load(venue);
  }, [venue, load]);

  useEffect(() => {
    return () => {
      tearDownCurrentMap();
    };
  }, [tearDownCurrentMap]);

  return (
    <div className="relative w-full h-full">
      <div
        ref={containerRef}
        id="venue-map-container"
        className="w-full h-full"
        style={{ background: '#0e1320' }}
      />
      {loading && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center z-10"
          style={{ background: 'rgba(14,19,32,0.9)', backdropFilter: 'blur(8px)' }}
        >
          <div className="w-10 h-10 border-[3px] border-primary border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm text-muted-foreground">Loading venue map...</p>
        </div>
      )}
      {error && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center z-10"
          style={{ background: 'rgba(14,19,32,0.9)' }}
        >
          <p className="text-sm text-destructive font-medium">Failed to load map</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xs text-center">{error}</p>
        </div>
      )}
    </div>
  );
}
